// Cart Page JavaScript
(function() {
  'use strict';

  // Cart API endpoints
  const CART_API = {
    get: '/cart.js',
    add: '/cart/add.js',
    update: '/cart/update.js',
    change: '/cart/change.js',
    clear: '/cart/clear.js'
  };

  // Free shipping threshold (in cents)
  const FREE_SHIPPING_THRESHOLD = 5000;

  // Format money helper
  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  // Cart state
  let cartState = {
    items: [],
    totalPrice: 0,
    itemCount: 0,
    discountCode: null,
    termsAccepted: false
  };
  
  // Event listener tracking
  let eventListenersSetup = false;
  let clickHandler = null;
  let removeHandler = null;
  
  // Debounce tracking
  let updateDebounceTimer = null;
  let pendingUpdates = new Map();
  let isUpdating = false;
  
  // Use global flag to coordinate with main-cart.liquid
  window.cartPageInitialLoadEmitted = window.cartPageInitialLoadEmitted || false;
  
  // Expose debug variables globally for troubleshooting
  window.cartPageDebug = {
    eventListenersSetup: () => eventListenersSetup,
    isUpdating: () => isUpdating,
    pendingUpdates: () => pendingUpdates.size,
    updateDebounceTimer: () => !!updateDebounceTimer
  };

  // Initialize cart page
  function initCartPage() {
    // Only run on cart page
    if (!document.querySelector('.cart-page-container')) return;

    // Setup event listeners
    setupQuantityButtons();
    setupRemoveButtons();
    setupDiscountCode();
    setupTermsCheckbox();
    setupCheckoutButton();
    
    // Initial cart fetch - fetchCart will fire cart:loaded event when done
    fetchCart();
    
    // Initial checkout button state
    updateCheckoutButton();
  }

  // Fetch current cart state
  async function fetchCart() {
    try {
      const response = await fetch(CART_API.get, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch cart');
      
      const cart = await response.json();
      updateCartState(cart);
      updateCartUI();
      
      // Fire cart:loaded event after cart data is successfully loaded (only once on cart page)
      if (!window.cartPageInitialLoadEmitted && window.location.pathname === '/cart') {
        window.cartPageInitialLoadEmitted = true;
        if (window.DEBUG_MODE) {
          console.log('[CartPage] Firing initial cart:loaded event from cart-page.js');
        }
        document.dispatchEvent(new CustomEvent('cart:loaded', {
          detail: {
            cart: cart,
            itemCount: cart.item_count || 0,
            totalPrice: cart.total_price || 0,
            source: 'cart-page-js'
          }
        }));
      }
    } catch (error) {
      if (window.DEBUG_MODE) console.error('Error fetching cart:', error);
      
      // Fire cart:loaded event even on error to prevent shipping calculator from hanging (only once)
      if (!window.cartPageInitialLoadEmitted && window.location.pathname === '/cart') {
        window.cartPageInitialLoadEmitted = true;
        if (window.DEBUG_MODE) {
          console.log('[CartPage] Firing cart:loaded event (error case)');
        }
        document.dispatchEvent(new CustomEvent('cart:loaded', {
          detail: {
            cart: null,
            itemCount: 0,
            totalPrice: 0,
            error: true,
            source: 'cart-page-js-error'
          }
        }));
      }
    }
  }

  // Update cart state from API response
  function updateCartState(cart) {
    cartState.items = cart.items || [];
    cartState.totalPrice = cart.total_price || 0;
    cartState.itemCount = cart.item_count || 0;
    
    // Check for existing discount
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      const discounts = cart.cart_level_discount_applications;
      const totalDiscountAmount = discounts.reduce((sum, d) => sum + d.total_allocated_amount, 0);
      const discountCodes = discounts.map(d => d.title);
      
      // Keep input enabled for adding more discounts
      const input = document.getElementById('discountCodeInput');
      const button = document.getElementById('applyDiscountButton');
      const message = document.getElementById('discountMessage');
      
      if (input && button) {
        // Keep input empty and enabled so users can add more discounts
        input.value = '';
        input.disabled = false;
        input.placeholder = 'Discount code';
        button.textContent = 'Apply';
        button.disabled = false;
      }
      
      if (message) {
        if (discounts.length === 1) {
          message.textContent = `${discounts.length} discount applied. You can add up to ${5 - discounts.length} more.`;
        } else {
          message.textContent = `${discounts.length} discounts applied. You can add up to ${5 - discounts.length} more.`;
        }
        message.className = 'discount-message success';
      }
      
      cartState.discountCode = discountCodes.join(',');
    }
  }

  // Update cart UI elements
  function updateCartUI() {
    updateCartSummary();
    updateCheckoutButton();
    
    // cart:updated event dispatched by refreshCartSection() with proper source tracking
    
    // Show/hide empty cart message
    const emptyMessage = document.getElementById('cartEmptyMessage');
    const itemsContainer = document.getElementById('cartItemsContainer');
    
    if (cartState.itemCount === 0) {
      if (emptyMessage) emptyMessage.style.display = 'block';
      if (itemsContainer) itemsContainer.style.display = 'none';
    } else {
      if (emptyMessage) emptyMessage.style.display = 'none';
      if (itemsContainer) itemsContainer.style.display = 'flex';
    }
  }


  // Update cart summary
  function updateCartSummary() {
    const elements = {
      totalItems: document.getElementById('cartTotalItems'),
      subtotal: document.getElementById('cartSubtotal'),
      shipping: document.getElementById('cartShipping'),
      total: document.getElementById('cartTotal')
    };
    
    if (elements.totalItems) {
      elements.totalItems.textContent = cartState.itemCount;
    }
    
    if (elements.subtotal) {
      elements.subtotal.textContent = formatMoney(cartState.totalPrice);
    }
    
    if (elements.shipping) {
      const amountRemaining = FREE_SHIPPING_THRESHOLD - cartState.totalPrice;
      elements.shipping.textContent = amountRemaining > 0 ? 'Calculated at checkout' : 'Calculating...';
    }
    
    if (elements.total) {
      elements.total.textContent = formatMoney(cartState.totalPrice);
    }
  }

  // Setup quantity button handlers
  function setupQuantityButtons() {
    // Only set up event listeners once
    if (eventListenersSetup) {
      if (window.DEBUG_MODE) {
        console.log('[CartPage] Event listeners already setup, skipping...');
      }
      return;
    }
    
    if (window.DEBUG_MODE) {
      console.log('[CartPage] Setting up quantity button handlers');
    }
    
    // Remove old handler if it exists
    if (clickHandler) {
      document.removeEventListener('click', clickHandler);
    }
    
    // Create new handler
    clickHandler = async function(e) {
      const button = e.target.closest('.cart-quantity-button');
      if (!button) return;
      
      // Prevent clicks while updating
      if (isUpdating) {
        if (window.DEBUG_MODE) {
          console.log('[CartPage] Update in progress, ignoring click');
        }
        e.preventDefault();
        return;
      }
      
      if (window.DEBUG_MODE) {
        console.log('Quantity button clicked:', button);
      }
      
      e.preventDefault();
      
      const line = parseInt(button.getAttribute('data-line'));
      const isIncrease = button.classList.contains('increase');
      
      if (window.DEBUG_MODE) {
        console.log('Line:', line, 'Increase:', isIncrease);
      }
      
      // Get current quantity
      const input = document.querySelector(`.cart-quantity-input[data-line="${line}"]`);
      if (!input) {
        if (window.DEBUG_MODE) console.error('Input not found for line:', line);
        return;
      }
      
      const currentQty = parseInt(input.value) || 1;
      const newQty = isIncrease ? currentQty + 1 : Math.max(0, currentQty - 1);
      
      if (window.DEBUG_MODE) {
        console.log('Updating quantity from', currentQty, 'to', newQty);
      }
      
      // Use debounced update
      debouncedUpdateQuantity(line, newQty);
    };
    
    // Add event listener
    document.addEventListener('click', clickHandler);
    eventListenersSetup = true;
  }

  // Setup remove button handlers
  function setupRemoveButtons() {
    // Only set up event listeners once
    if (removeHandler) {
      return; // Already setup
    }
    
    // Remove old handler if it exists
    if (removeHandler) {
      document.removeEventListener('click', removeHandler);
    }
    
    // Create new handler
    removeHandler = async function(e) {
      const button = e.target.closest('.cart-item-remove');
      if (!button) return;
      
      // Prevent clicks while updating
      if (isUpdating) {
        e.preventDefault();
        return;
      }
      
      e.preventDefault();
      const line = parseInt(button.getAttribute('data-line'));
      
      await updateQuantity(line, 0);
    };
    
    // Add event listener
    document.addEventListener('click', removeHandler);
  }

  // Debounced update quantity function
  function debouncedUpdateQuantity(line, quantity) {
    // Clear any existing timer
    if (updateDebounceTimer) {
      clearTimeout(updateDebounceTimer);
    }
    
    // Store the pending update
    pendingUpdates.set(line, quantity);
    
    if (window.DEBUG_MODE) {
      console.log(`[CartPage] Debouncing update for line ${line} to quantity ${quantity}`);
    }
    
    // Set new timer
    updateDebounceTimer = setTimeout(async () => {
      if (isUpdating) {
        if (window.DEBUG_MODE) {
          console.log('[CartPage] Already updating, will retry...');
        }
        // Retry after 500ms
        setTimeout(() => debouncedUpdateQuantity(line, quantity), 500);
        return;
      }
      
      // Process all pending updates
      const updates = Array.from(pendingUpdates.entries());
      pendingUpdates.clear();
      
      if (window.DEBUG_MODE) {
        console.log('[CartPage] Processing', updates.length, 'pending updates');
      }
      
      // Execute updates sequentially to prevent race conditions
      for (const [updateLine, updateQty] of updates) {
        await updateQuantity(updateLine, updateQty);
      }
    }, 300); // 300ms debounce
  }

  // Update item quantity
  async function updateQuantity(line, quantity) {
    // Prevent concurrent updates
    if (isUpdating) {
      if (window.DEBUG_MODE) {
        console.log('[CartPage] Update already in progress, skipping...');
      }
      return;
    }
    
    isUpdating = true;
    
    try {
      // Disabled: No longer enforcing maximum quantity limits
      // Users can add unlimited quantities
      
      // Show loading state
      showLoadingState();
      
      if (window.DEBUG_MODE) {
        console.log(`[CartPage] Updating line ${line} to quantity ${quantity}`);
      }
      
      const response = await fetch(CART_API.change, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity // No quantity limit
        })
      });
      
      if (!response.ok) throw new Error('Failed to update cart');
      
      const cart = await response.json();
      updateCartState(cart);
      
      // Refresh the entire cart section (but don't fire duplicate events)
      await refreshCartSection(false); // Pass false to prevent duplicate event
      
      // Fire only ONE event after everything is complete
      if (quantity === 0) {
        document.dispatchEvent(new CustomEvent('cart:product_removed', {
          detail: { line, quantity, source: 'cart-page-js' }
        }));
      } else {
        document.dispatchEvent(new CustomEvent('cart:quantity_update', {
          detail: { line, quantity, source: 'cart-page-js' }
        }));
      }
      
      if (window.DEBUG_MODE) {
        console.log(`[CartPage] Update complete for line ${line}`);
      }
      
    } catch (error) {
      if (window.DEBUG_MODE) console.error('Error updating quantity:', error);
      hideLoadingState();
    } finally {
      isUpdating = false;
    }
  }

  // Refresh cart section
  async function refreshCartSection(fireEvent = true) {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=main-cart`);
      const html = await response.text();
      
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');
      const newContainer = newDoc.querySelector('.cart-page-container');
      
      if (newContainer) {
        const currentContainer = document.querySelector('.cart-page-container');
        if (currentContainer) {
          currentContainer.replaceWith(newContainer);
          
          // NOTE: Don't re-initialize event listeners since we're now using global event delegation
          // This prevents duplicate event listeners which was causing the cascade of API calls
          
          // Only fire event if requested (to prevent duplicate events)
          if (fireEvent) {
            document.dispatchEvent(new CustomEvent('cart:quantity_update', {
              detail: {
                source: 'section_refresh'
              }
            }));
          }
        }
      }
      
      hideLoadingState();
    } catch (error) {
      if (window.DEBUG_MODE) console.error('Error refreshing cart:', error);
      window.location.reload();
    }
  }

  // Setup discount code functionality
  function setupDiscountCode() {
    const applyButton = document.getElementById('applyDiscountButton');
    const input = document.getElementById('discountCodeInput');
    const message = document.getElementById('discountMessage');
    
    if (!applyButton || !input) return;
    
    applyButton.addEventListener('click', async function() {
      const code = input.value.trim().toUpperCase();
      if (!code) {
        if (message) {
          message.textContent = 'Please enter a discount code';
          message.className = 'discount-message error';
        }
        return;
      }
      
      try {
        // Disable button and show loading state
        applyButton.disabled = true;
        applyButton.textContent = 'Applying...';
        
        if (message) {
          message.textContent = 'Validating discount code...';
          message.className = 'discount-message';
        }
        
        // Apply discount code using proper Shopify method
        // The correct way is to redirect to /discount/CODE which validates and redirects back to cart
        
        try {
          // First, try the direct discount URL approach
          const discountUrl = `/discount/${encodeURIComponent(code)}`;
          const response = await fetch(discountUrl, {
            method: 'GET',
            redirect: 'manual' // Don't follow redirects automatically
          });
          
          // Shopify returns a 302 redirect if the discount is valid
          if (response.status === 302 || response.status === 301) {
            // Discount is valid, now check cart to see if it was applied
            const cartResponse = await fetch('/cart.js');
            const cartData = await cartResponse.json();
            
            if (window.DEBUG_MODE) {
              console.log('Cart data after discount application:', cartData);
            }
            
            // Check for discount in various possible locations
            let discountAmount = 0;
            let discountTitle = code;
            let hasDiscount = false;
            
            // Check cart_level_discount_applications (most common)
            if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
              const discount = cartData.cart_level_discount_applications[0];
              discountAmount = discount.total_allocated_amount;
              discountTitle = discount.title || code;
              hasDiscount = true;
            } 
            // Check total_discount
            else if (cartData.total_discount > 0) {
              discountAmount = cartData.total_discount;
              hasDiscount = true;
            }
            // Check discount_codes array
            else if (cartData.discount_codes && cartData.discount_codes.length > 0) {
              discountAmount = cartData.total_discount || 0;
              hasDiscount = true;
            }
            
            if (hasDiscount) {
              // Success!
              if (message) {
                message.textContent = `Discount "${discountTitle}" applied! You saved ${formatMoney(discountAmount)}`;
                message.className = 'discount-message success';
              }
              
              cartState.discountCode = code;
              input.disabled = true;
              applyButton.textContent = 'Applied';
              applyButton.disabled = true;
              
              // Refresh cart display
              await fetchCart();
            } else {
              throw new Error('Discount code was accepted but no discount was applied');
            }
            
          } else if (response.status === 404) {
            throw new Error('Invalid or expired discount code');
          } else {
            // Try alternative method - form submission to /cart
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/cart';
            form.style.display = 'none';
            
            const formType = document.createElement('input');
            formType.type = 'hidden';
            formType.name = 'form_type';
            formType.value = 'cart';
            form.appendChild(formType);
            
            const utf8 = document.createElement('input');
            utf8.type = 'hidden';
            utf8.name = 'utf8';
            utf8.value = '✓';
            form.appendChild(utf8);
            
            const discount = document.createElement('input');
            discount.type = 'hidden';
            discount.name = 'discount';
            discount.value = code;
            form.appendChild(discount);
            
            document.body.appendChild(form);
            
            // Submit form and check result
            const formData = new FormData(form);
            const formResponse = await fetch('/cart', {
              method: 'POST',
              body: formData,
              redirect: 'manual'
            });
            
            document.body.removeChild(form);
            
            // Check cart again
            const cartResponse = await fetch('/cart.js');
            const cartData = await cartResponse.json();
            
            if (cartData.cart_level_discount_applications?.length > 0 || cartData.total_discount > 0) {
              // Success with form method
              const discountApp = cartData.cart_level_discount_applications?.[0];
              const amount = discountApp?.total_allocated_amount || cartData.total_discount;
              const title = discountApp?.title || code;
              
              if (message) {
                message.textContent = `Discount "${title}" applied! You saved ${formatMoney(amount)}`;
                message.className = 'discount-message success';
              }
              
              cartState.discountCode = code;
              input.disabled = true;
              applyButton.textContent = 'Applied';
              applyButton.disabled = true;
              
              await fetchCart();
            } else {
              throw new Error('Invalid or expired discount code');
            }
          }
          
        } catch (networkError) {
          if (window.DEBUG_MODE) console.error('Network error applying discount:', networkError);
          throw new Error('Invalid or expired discount code');
        }
        
      } catch (error) {
        if (window.DEBUG_MODE) console.error('Discount error:', error);
        
        if (message) {
          message.textContent = error.message || 'Invalid discount code. Please check and try again.';
          message.className = 'discount-message error';
        }
        
        // Re-enable button
        applyButton.disabled = false;
        applyButton.textContent = 'Apply';
        
        // Clear the input
        input.value = '';
        cartState.discountCode = null;
      }
    });
    
    // Also handle Enter key in input
    input.addEventListener('keypress', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (applyButton) {
          applyButton.click();
        }
      }
    });
  }

  // Setup terms checkbox
  function setupTermsCheckbox() {
    const checkbox = document.getElementById('termsCheckbox');
    const error = document.getElementById('termsError');
    
    if (!checkbox) return;
    
    checkbox.addEventListener('change', function() {
      cartState.termsAccepted = this.checked;
      
      if (error) {
        error.classList.toggle('show', !this.checked);
      }
      
      updateCheckoutButton();
    });
  }

  // Setup checkout button
  function setupCheckoutButton() {
    const button = document.getElementById('checkoutButton');
    if (!button) return;
    
    button.addEventListener('click', function(e) {
      if (!cartState.termsAccepted) {
        e.preventDefault();
        
        const checkbox = document.getElementById('termsCheckbox');
        const error = document.getElementById('termsError');
        
        if (checkbox) checkbox.focus();
        if (error) error.classList.add('show');
        
        return;
      }
      
      // Proceed to checkout
      window.location.href = '/checkout';
    });
  }

  // Update checkout button state
  function updateCheckoutButton() {
    const button = document.getElementById('checkoutButton');
    if (!button) return;
    
    const isDisabled = cartState.itemCount === 0 || !cartState.termsAccepted;
    button.disabled = isDisabled;
    
    // Debug log
    if (window.DEBUG_MODE) {
      console.log('Checkout button update:', {
        itemCount: cartState.itemCount,
        termsAccepted: cartState.termsAccepted,
        disabled: isDisabled
      });
    }
  }

  // Show loading state
  function showLoadingState() {
    // Create overlay if it doesn't exist
    let overlay = document.getElementById('cartLoadingOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'cartLoadingOverlay';
      overlay.className = 'cart-loading-overlay';
      overlay.innerHTML = '<div class="cart-loading-spinner"></div>';
      document.body.appendChild(overlay);
    }
    overlay.style.display = 'flex';
    
    const container = document.querySelector('.cart-page-container');
    if (container) {
      container.classList.add('cart-loading');
    }
  }

  // Hide loading state
  function hideLoadingState() {
    const overlay = document.getElementById('cartLoadingOverlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
    
    const container = document.querySelector('.cart-page-container');
    if (container) {
      container.classList.remove('cart-loading');
    }
  }

  // Show notification message
  function showNotification(message, type = 'info') {
    // Remove any existing notification
    const existingNotification = document.querySelector('.cart-notification');
    if (existingNotification) {
      existingNotification.remove();
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `cart-notification cart-notification--${type}`;
    notification.innerHTML = `
      <div class="cart-notification__content">
        <span class="cart-notification__message">${message}</span>
        <button class="cart-notification__close" aria-label="Close notification">&times;</button>
      </div>
    `;
    
    // Add to cart container
    const cartContainer = document.querySelector('.cart-page-container');
    if (cartContainer) {
      cartContainer.insertBefore(notification, cartContainer.firstChild);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        notification.classList.add('cart-notification--fade-out');
        setTimeout(() => notification.remove(), 300);
      }, 5000);
      
      // Close button handler
      notification.querySelector('.cart-notification__close').addEventListener('click', () => {
        notification.remove();
      });
    }
  }

  // Format money (cents to dollars)
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartPage);
  } else {
    initCartPage();
  }

  // Export for external use
  window.FiggyzCart = {
    refresh: fetchCart,
    updateQuantity: updateQuantity
  };

})();